import React, { useEffect, useState } from 'react';
import { useTranslation } from '../i18n/LanguageContext';
import { useSmsTools, useSmsLog, smsLogDetail, SMS_ACCOUNTS } from '../modules/sms';
import { SecurityAlertFields } from '../modules/security';
import { http } from '../utils/apiHttp';

// Ayarlar > SMS (yalnızca sistem admini) — ortak SMS modülünün (modules/sms, ServisPro'dan birebir)
// TrendSniper arayüzü: NetGSM bilgileri (şifreler write-only), Test SMS, Kalan SMS Adedi ve
// SMS Gönderim Raporu. SMS yalnızca sistem mesajları (güvenlik bildirimi, 2FA kodu) için kullanılır;
// şablon/olay yok. Backend: /api/admin/sms (backend/src/sms.js).

const BASE = '/api/admin/sms';

const ACCOUNT_LABELS = { OTP: 'OTP', BIREYSEL: 'Bireysel', TACIR: 'Tacir' };
const PASSWORDS = [
  ['netgsm_otp_password', 'otpPassword'],
  ['netgsm_password', 'individualPassword'],
  ['netgsm_tacir_password', 'merchantPassword'],
];

const isoDay = (d) => d.toISOString().slice(0, 10);

export default function SmsSettings({ token }) {
  const { t, lang } = useTranslation();

  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ netgsm_username: '', netgsm_header: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () =>
    http.get(`${BASE}/config`).then(({ data }) => {
      setCfg(data);
      setForm({ netgsm_username: data.netgsm_username || '', netgsm_header: data.netgsm_header || '' });
    }).catch((err) => setMsg({ type: 'error', text: err.message }));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await http.put(`${BASE}/config`, form);
      setMsg({ type: 'ok', text: t('sms.saved') });
      setForm((f) => ({ ...f, netgsm_password: '', netgsm_tacir_password: '', netgsm_otp_password: '' }));
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const clearPassword = async (key) => {
    if (!window.confirm(t('sms.clearConfirm'))) return;
    try { await http.put(`${BASE}/config`, { [`clear_${key}`]: true }); await load(); }
    catch (err) { setMsg({ type: 'error', text: err.message }); }
  };

  const tools = useSmsTools({
    http,
    basePath: BASE,
    notify: (text, variant) => setMsg({ type: variant === 'danger' ? 'error' : 'ok', text }),
    messages: {
      testSent: t('sms.testSent'),
      testViaFallback: t('sms.testViaFallback'),
      testFailed: t('sms.testFailed'),
      balanceFailed: t('sms.balanceFailed'),
      rawSuffix: (raw) => `(${raw})`,
    },
  });

  const today = new Date();
  const log = useSmsLog({
    http,
    path: `${BASE}/sms-log`,
    initialStartDate: isoDay(new Date(today.getTime() - 30 * 86400000)),
    initialEndDate: isoDay(today),
  });

  // Güvenlik bildirim telefonu (madde 11) — /api/admin/security; modülün SecurityAlertFields'i.
  const [alertPhone, setAlertPhone] = useState('');
  const [alertTesting, setAlertTesting] = useState(false);
  useEffect(() => {
    http.get('/api/admin/security/alert-phone').then(({ data }) => setAlertPhone(data.phone || '')).catch(() => {});
  }, []); // eslint-disable-line
  const saveAlertPhone = async () => {
    try { await http.put('/api/admin/security/alert-phone', { phone: alertPhone }); setMsg({ type: 'ok', text: t('sms.alertSaved') }); }
    catch (err) { setMsg({ type: 'error', text: err.message }); }
  };
  const testAlert = async () => {
    setAlertTesting(true);
    try {
      await http.put('/api/admin/security/alert-phone', { phone: alertPhone });
      await http.post('/api/admin/security/alert-test');
      setMsg({ type: 'ok', text: t('sms.alertTestSent') });
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setAlertTesting(false);
    }
  };

  const fmtDate = (iso) => new Date(iso).toLocaleString(lang === 'en' ? 'en-GB' : 'tr-TR');

  return (
    <div className="sms-settings">
      <p className="settings-note">{t('sms.note')}</p>

      <h4 className="sms-section-title">{t('sms.netgsmTitle')}</h4>
      {[['netgsm_username', 'username'], ['netgsm_header', 'header']].map(([key, label]) => (
        <div className="settings-field" key={key}>
          <label className="settings-label">{t(`sms.${label}`)}</label>
          <div className="settings-input-row">
            <input className="settings-input" value={form[key] ?? ''} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
          </div>
        </div>
      ))}
      {PASSWORDS.map(([key, label]) => (
        <div className="settings-field" key={key}>
          <label className="settings-label">{t(`sms.${label}`)}</label>
          <span className="settings-hint">{cfg?.[`${key}_set`] ? t('sms.passwordSet') : t('sms.passwordNotSet')}</span>
          <div className="settings-input-row">
            <input
              className="settings-input"
              type="password"
              autoComplete="new-password"
              placeholder={cfg?.[`${key}_set`] ? t('sms.keepPlaceholder') : ''}
              value={form[key] ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            />
            {cfg?.[`${key}_set`] && (
              <button type="button" className="btn btn-stop" onClick={() => clearPassword(key)}>{t('sms.clear')}</button>
            )}
          </div>
        </div>
      ))}
      <span className="settings-hint">{t('sms.iysNote')}</span>
      <div className="settings-footer" style={{ position: 'static' }}>
        {msg && <span className={`settings-msg settings-msg-${msg.type}`}>{msg.text}</span>}
        <button className="btn btn-start" style={{ minWidth: 120 }} onClick={save} disabled={saving}>
          {saving ? t('settings.saving') : t('settings.save')}
        </button>
      </div>

      <SecurityAlertFields
        editable
        phoneInput={
          <div className="settings-input-row">
            <input className="settings-input" placeholder="5XX XXX XX XX" value={alertPhone} onChange={(e) => setAlertPhone(e.target.value)} />
            <button type="button" className="btn" onClick={saveAlertPhone}>{t('settings.save')}</button>
          </div>
        }
        onTest={testAlert}
        testDisabled={!alertPhone || alertTesting}
        strings={{
          title: t('sms.alertTitle'),
          hint: t('sms.alertHint'),
          phoneLabel: t('sms.alertPhone'),
          testButton: t('sms.alertTest'),
        }}
        classNames={{ title: 'sms-section-title', hint: 'settings-hint', button: 'btn btn-start' }}
      />

      <h4 className="sms-section-title">{t('sms.testTitle')}</h4>
      <div className="settings-field">
        <div className="settings-input-row">
          <input className="settings-input" placeholder="5XX XXX XX XX" value={tools.testPhone} onChange={(e) => tools.setTestPhone(e.target.value)} />
          <select className="settings-input" style={{ maxWidth: 130 }} value={tools.testAccount} onChange={(e) => tools.setTestAccount(e.target.value)}>
            {SMS_ACCOUNTS.map((a) => <option key={a} value={a}>{ACCOUNT_LABELS[a]}</option>)}
          </select>
          <button className="btn btn-start" onClick={tools.sendTest} disabled={tools.testLoading || !tools.testPhone}>
            {tools.testLoading ? '…' : t('sms.testSend')}
          </button>
        </div>
      </div>
      <div className="settings-field">
        <div className="settings-input-row">
          <button className="btn" onClick={tools.checkBalance} disabled={tools.balanceLoading}>
            {tools.balanceLoading ? '…' : t('sms.balance')}
          </button>
          {tools.balance && <span className="settings-hint">{tools.balance}</span>}
        </div>
      </div>

      <h4 className="sms-section-title">{t('sms.logTitle')}</h4>
      <div className="settings-input-row" style={{ flexWrap: 'wrap', gap: 6 }}>
        <input className="settings-input" type="date" value={log.startDate} onChange={(e) => log.setStartDate(e.target.value)} />
        <input className="settings-input" type="date" value={log.endDate} onChange={(e) => log.setEndDate(e.target.value)} />
        <select className="settings-input" value={log.status} onChange={(e) => log.setStatus(e.target.value)}>
          <option value="">{t('sms.allStatuses')}</option>
          <option value="SENT">{t('sms.sent')}</option>
          <option value="FAILED">{t('sms.failed')}</option>
        </select>
      </div>
      <p className="settings-hint">
        {log.loading ? '…' : t('sms.logSummary', { sent: log.sentCount, failed: log.failedCount })}
      </p>
      <div className="sms-log-table-wrap">
        <table className="sms-log-table">
          <thead>
            <tr><th>{t('sms.colDate')}</th><th>{t('sms.colPurpose')}</th><th>{t('sms.colPhone')}</th><th>{t('sms.colStatus')}</th><th>{t('sms.colDetail')}</th></tr>
          </thead>
          <tbody>
            {log.rows.map((r) => (
              <tr key={r.id} title={r.message}>
                <td>{fmtDate(r.createdAt)}</td>
                <td>{r.purpose} · {ACCOUNT_LABELS[r.account] || r.account}</td>
                <td>{r.phone}</td>
                <td className={r.status === 'SENT' ? 'sms-ok' : 'sms-fail'}>{r.status === 'SENT' ? t('sms.sent') : t('sms.failed')}</td>
                <td>{smsLogDetail(r)}</td>
              </tr>
            ))}
            {!log.loading && log.rows.length === 0 && (
              <tr><td colSpan={5} className="settings-hint">{t('sms.noLogs')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
